/**
 * Fitting an email into its viewer. The viewer used to scale the whole
 * message down to its widest line - so one long unbroken link (a mail
 * scanner's rewritten URL) or a big pasted screenshot made the entire email
 * tiny. Now long words and links wrap and images fit the width first; only a
 * layout that is really wider (a fixed-width newsletter table) is scaled, and
 * never below a readable floor - past that it scrolls sideways instead.
 */
const STYLE_ID = 'oms-email-fit'

const CSS = `
  body { overflow-wrap: anywhere; word-break: break-word; }
  td, th, div, p, span, a, li { overflow-wrap: anywhere; }
  img { max-width: 100% !important; height: auto !important; }
  pre, code { white-space: pre-wrap !important; }
`

/** Make text and images fit the width; call once the email is in the frame. */
export function prepareEmail(doc) {
  if (!doc || !doc.head || doc.getElementById(STYLE_ID))
    return
  const style = doc.createElement('style')
  style.id = STYLE_ID
  style.textContent = CSS
  doc.head.appendChild(style)
}

/**
 * The zoom (percent) that fits what is left too wide into `width`, or null when it fits.
 * Never below `min`: smaller than that is unreadable, so it scrolls instead.
 */
export function fitZoom(iframe, width, min = 50) {
  const doc = iframe && iframe.contentDocument
  const body = doc && doc.body
  if (!body || !width)
    return null
  // measured at the screen's own width: a frame already zoomed out is laid out wider
  iframe.style.width = width + 'px'
  iframe.style.webkitTransform = 'none'
  const wide = Math.max(body.scrollWidth, doc.documentElement ? doc.documentElement.scrollWidth : 0)
  if (wide <= width + 2)
    return null
  return Math.max(min, Math.floor((width / wide) * 100) - 2)
}

/** Call `fn` each time an image in the email finishes loading (they change the width). */
export function onImagesLoaded(doc, fn) {
  if (!doc)
    return
  for (const img of doc.getElementsByTagName('img'))
    if (!img.complete)
      img.addEventListener('load', fn, {once: true})
}
